import { endOfMonth, format, parse } from 'date-fns';
import { getDateRangesInMsFromDaysBack } from './getDateRangesInMsFromDaysBack';

const DATE_FNS_FORMAT = 'yyyyMMdd';
const MAX_MONTHS = 12;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * @description Generates month options needed for the TimeFrameDropdown
 */
export const getMonthsForTimeFrameDropdown = () => {
  const monthDropdownOptions = [];
  const currentDate = new Date();
  currentDate.setDate(1);

  for (let i = 0; i < MAX_MONTHS; i++) {
    monthDropdownOptions.push({
      label: `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`,
      value: format(currentDate, DATE_FNS_FORMAT),
    });

    currentDate.setMonth(currentDate.getMonth() - 1);
  }

  return monthDropdownOptions;
};

/**
 * @description Helper function to be used with TimeFrameDropdown when passing dates as params to endpoints.
 * Returns start and end dates formatted as yyyyMMdd in local timezone
 */
export const getDateRangesFromDaysBackOrStartDate = (daysBack: number, startDate?: string) => {
  if (startDate) {
    return getDateRangesFromStartDate(startDate);
  }

  return getDateRangesFromDaysBack(daysBack);
};

/**
 * @description Helper function for React Query endpoints that expect millisecond timestamps.
 * Returns start and end dates as millisecond timestamps in local timezone
 */
export const getDateRangesInMsFromDaysBackOrStartDate = (
  daysBack: number,
  startDate?: string,
): { startDateMs: number; endDateMs: number } => {
  if (startDate) {
    return getDateRangesInMsFromStartDate(startDate);
  }

  return getDateRangesInMsFromDaysBack(daysBack);
};

/**
 * @description Helper function to be used with TimeFrameDropdown when passing dates as params to endpoints.
 * @param startDate yyyyMMdd
 * Returns start and end dates formatted as yyyyMMdd
 */
const getDateRangesFromStartDate = (startDate: string): { startDate: string; endDate: string } => {
  const parsedStartDate = parse(startDate, DATE_FNS_FORMAT, new Date());

  const endOfMonthDate = endOfMonth(parsedStartDate);
  const endDateFormatted = format(endOfMonthDate, DATE_FNS_FORMAT);

  return {
    startDate: format(parsedStartDate, DATE_FNS_FORMAT),
    endDate: endDateFormatted,
  };
};

/**
 * @description Helper function to be used with TimeFrameDropdown when passing dates as params to endpoints.
 * Returns start and end dates formatted as yyyyMMdd
 */
const getDateRangesFromDaysBack = (daysBack: number): { startDate: string; endDate: string } => {
  const now = new Date();

  // Calculate start date (N days back at local midnight)
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - daysBack);
  startDate.setHours(0, 0, 0, 0);

  // Calculate end date (today at local midnight)
  const endDate = new Date(now);
  endDate.setHours(0, 0, 0, 0);

  return {
    startDate: format(startDate, DATE_FNS_FORMAT),
    endDate: format(endDate, DATE_FNS_FORMAT),
  };
};

/**
 * @description Helper function to return date ranges as milliseconds from a start date string.
 * @param startDate yyyyMMdd
 * Returns start and end dates as millisecond timestamps
 */
const getDateRangesInMsFromStartDate = (startDate: string): { startDateMs: number; endDateMs: number } => {
  const parsedStartDate = parse(startDate, DATE_FNS_FORMAT, new Date());
  parsedStartDate.setHours(0, 0, 0, 0);

  // End of the month at 23:59:59.999
  const endOfMonthDate = endOfMonth(parsedStartDate);
  endOfMonthDate.setHours(23, 59, 59, 999);

  return {
    startDateMs: parsedStartDate.getTime(),
    endDateMs: endOfMonthDate.getTime(),
  };
};
